import React, { useCallback, useEffect, useRef } from 'react';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { EditorContent, EditorProvider, useCurrentEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

import { Heading } from '~/components/core';
import MenuBar from '~/components/core/Editor/MenuBar';
import Text from '~/components/core/TextComponents/Text';

import SopProcessingLoader from './SopProcessingLoader';
import type { SopDocumentReviewStepProps } from './types';

const SYNC_THROTTLE_MS = 500;

// Inner component that has access to editor context
const EditorInner: React.FC<{
  markdown_pages: string[];
  onContentChange: (editedPages: string[]) => void;
}> = ({ markdown_pages, onContentChange }) => {
  const { editor } = useCurrentEditor();
  const initializedRef = useRef(false);
  const lastSyncTimeRef = useRef(0);

  // Combine pages with page break markers (horizontal rules)
  const combinePagesWithBreaks = (pages: string[]): string => {
    return pages.join('\n\n---\n\n');
  };

  // Split document back to pages array
  const splitDocumentToPages = (markdown: string): string[] => {
    return markdown.split(/\n\s*---\s*\n/).map((page) => page.trim());
  };

  // Sync editor content to parent state
  const syncContentToParent = useCallback(() => {
    if (!editor) return;
    const currentMarkdown = editor.storage.markdown.getMarkdown();
    const pagesArray = splitDocumentToPages(currentMarkdown);
    onContentChange(pagesArray);
    lastSyncTimeRef.current = Date.now();
  }, [editor, onContentChange]);

  // Throttled sync - limits updates to max once per SYNC_THROTTLE_MS
  const throttledSync = useCallback(() => {
    const now = Date.now();
    if (now - lastSyncTimeRef.current >= SYNC_THROTTLE_MS) {
      syncContentToParent();
    }
  }, [syncContentToParent]);

  // Load markdown pages when component mounts (received from dialog after OCR)
  useEffect(() => {
    if (markdown_pages && editor && !initializedRef.current) {
      const combined = combinePagesWithBreaks(markdown_pages);
      editor.commands.setContent(combined, false);
      initializedRef.current = true;
    }
  }, [markdown_pages, editor]);

  // Sync on content updates (throttled) to keep parent state current
  // This prevents race conditions when "Next" button is clicked
  useEffect(() => {
    if (!editor) return;

    editor.on('update', throttledSync);

    return () => {
      editor.off('update', throttledSync);
    };
  }, [editor, throttledSync]);

  return (
    <EditorContent
      editor={editor}
      className="prose max-w-none p-4 [&_hr]:my-24 [&_hr]:border-0 [&_hr]:border-t-2 [&_hr]:border-dashed [&_hr]:border-slate-300"
    />
  );
};

const SopDocumentReviewStep: React.FC<SopDocumentReviewStepProps> = ({
  markdown_pages,
  onContentChange,
  isLoading,
}) => {
  const extensions = [
    StarterKit,
    Markdown, // Enable markdown import/export
    Underline, // Required for textStyle toolbar
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }), // Required for align toolbar
  ];

  // Show loading state during OCR processing
  if (isLoading) {
    return <SopProcessingLoader />;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <Heading variant={Heading.TYPES.H2} colorVariant={Heading.COLOR_VARIANTS.PRIMARY}>
          Internal SOP Content Extraction
        </Heading>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          Please review Clive’s SOP content extraction.
        </Text>
      </div>

      <div className="max-h-[450px] w-full overflow-y-auto rounded-lg border border-solid border-slate-300 px-4">
        <EditorProvider
          extensions={extensions}
          slotBefore={
            <div className="sticky top-0 z-10 bg-white pb-0">
              <MenuBar suggestionOptions={[]} displayConfig={{ headings: true, textStyle: true, align: true }} />
            </div>
          }
        >
          <EditorInner markdown_pages={markdown_pages} onContentChange={onContentChange} />
        </EditorProvider>
      </div>
    </div>
  );
};

export default SopDocumentReviewStep;
